import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { EmptyState, ErrorState, PageLoader } from '../components/states';
import { MetricCard } from '../components/subscriptions';
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  ConfirmDialog,
  DefinitionItem,
  FormField,
  InlineNotice,
  PageHeading,
} from '../components/ui';
import { api, getErrorMessage, type AdminSubscriptionFilters } from '../lib/api';
import {
  formatAddress,
  formatDate,
  formatDateTime,
  formatMoney,
  frequencyLabel,
  subscriptionStatusLabels,
  subscriptionStatusTone,
} from '../lib/format';
import {
  adminSettingsInputSchema,
  type AdminPlan,
  type AdminPlanInput,
  type CreateIyzicoPricingPlanInput,
  type AdminSettingsInput,
  type OrderSyncIssue,
  type PaymentIssue,
} from '../lib/schemas';

export function AdminDashboardPage() {
  const query = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: ({ signal }) => api.admin.getDashboard(signal),
    refetchInterval: 60_000,
  });

  if (query.isPending) return <PageLoader label="Operasyon özeti hazırlanıyor…" />;
  if (query.isError) {
    return (
      <ErrorState
        message={getErrorMessage(query.error)}
        onRetry={() => void query.refetch()}
        title="Yönetim özeti alınamadı"
      />
    );
  }

  const data = query.data;
  return (
    <div className="admin-page page-stack">
      <PageHeading
        actions={
          <Button onClick={() => void query.refetch()} size="small" variant="secondary">
            <RefreshCw aria-hidden="true" size={16} /> Yenile
          </Button>
        }
        description="Tahsilat ve sipariş akışındaki kritik işleri tek bakışta izleyin."
        eyebrow="Operasyon merkezi"
        title="Bugünün genel görünümü"
      />
      <section aria-label="Önemli metrikler" className="metrics-grid">
        <MetricCard
          detail="Doğrulanmış ve devam eden"
          label="Aktif abonelik"
          value={String(data.activeSubscriptions)}
        />
        <MetricCard
          detail="Kart güncellemesi bekliyor"
          label="Ödeme gerekli"
          tone={data.pastDueSubscriptions > 0 ? 'warning' : 'default'}
          value={String(data.pastDueSubscriptions)}
        />
        <MetricCard
          detail="Sunucudan doğrulanan"
          label="Bugünkü tahsilat"
          value={String(data.successfulPaymentsToday)}
        />
        <MetricCard
          detail="Bu ay, TRY"
          label="Abonelik geliri"
          value={formatMoney(data.monthlyRevenueMinor)}
        />
        <MetricCard
          detail="Kuyrukta veya işleniyor"
          label="Sipariş bekliyor"
          tone={data.pendingOrderSync > 0 ? 'warning' : 'default'}
          value={String(data.pendingOrderSync)}
        />
        <MetricCard
          detail="Operasyon kontrolü gerekli"
          label="Aktarım hatası"
          tone={data.failedOrderSync > 0 ? 'danger' : 'default'}
          value={String(data.failedOrderSync)}
        />
      </section>

      <div className="admin-dashboard-grid">
        <Card className="operations-card">
          <div className="card-title-row">
            <div>
              <p className="eyebrow">İş sırası</p>
              <h2>Hızlı operasyonlar</h2>
            </div>
            <Settings2 aria-hidden="true" size={22} />
          </div>
          <nav aria-label="Hızlı operasyonlar" className="operation-links">
            <Link to="/admin/payments">
              <span>
                <strong>Ödeme sorunları</strong>
                <small>{data.pastDueSubscriptions} abonelik takip bekliyor</small>
              </span>
              <ArrowRight aria-hidden="true" size={18} />
            </Link>
            <Link to="/admin/order-sync">
              <span>
                <strong>Sipariş aktarımı</strong>
                <small>{data.failedOrderSync} hata operasyon kontrolünde</small>
              </span>
              <ArrowRight aria-hidden="true" size={18} />
            </Link>
            <Link to="/admin/plans">
              <span>
                <strong>Plan eşlemeleri</strong>
                <small>Shopify varyant ve abonelik planlarını yönet</small>
              </span>
              <ArrowRight aria-hidden="true" size={18} />
            </Link>
          </nav>
        </Card>
        <Card className="webhook-card">
          <div className="card-title-row">
            <div>
              <p className="eyebrow">Teknik izleme</p>
              <h2>Son webhook hataları</h2>
            </div>
            <TriangleAlert aria-hidden="true" size={22} />
          </div>
          {data.recentWebhookErrors.length === 0 ? (
            <div className="compact-empty">
              <CheckCircle2 aria-hidden="true" size={22} /> Son webhook hatası yok.
            </div>
          ) : (
            <ul className="event-list">
              {data.recentWebhookErrors.map((event) => (
                <li key={event.id}>
                  <div>
                    <strong>{event.eventType}</strong>
                    <span>{event.message}</span>
                  </div>
                  <small>{formatDateTime(event.occurredAt)}</small>
                  <code>{event.correlationId}</code>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function liraToMinor(value: string): number | undefined {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(?:\.\d{1,2})?$/u.test(normalized)) return undefined;
  const amount = Number(normalized);
  return Number.isSafeInteger(Math.round(amount * 100)) ? Math.round(amount * 100) : undefined;
}

function istanbulDayBoundary(value: string, endOfDay = false): string {
  return new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}+03:00`).toISOString();
}

interface AdminPlanEditorProps {
  editingPlan?: AdminPlan | undefined;
  onCancelEdit: () => void;
}

function AdminPlanEditor({ editingPlan, onCancelEdit }: AdminPlanEditorProps) {
  const queryClient = useQueryClient();
  const catalogQuery = useQuery({
    queryKey: ['admin', 'plan-catalog'],
    queryFn: ({ signal }) => api.admin.getPlanCatalog(signal),
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shopifyVariantGid, setShopifyVariantGid] = useState('');
  const [iyzicoProductReferenceCode, setIyzicoProductReferenceCode] = useState('');
  const [iyzicoPricingPlanReferenceCode, setIyzicoPricingPlanReferenceCode] = useState('');
  const [shippingPriceTry, setShippingPriceTry] = useState('');
  const [active, setActive] = useState(true);
  const [formError, setFormError] = useState<string>();
  const [providerProductName, setProviderProductName] = useState('');
  const [providerProductDescription, setProviderProductDescription] = useState('');
  const [providerPlanName, setProviderPlanName] = useState('');
  const [providerPlanAmountTry, setProviderPlanAmountTry] = useState('');
  const [providerPlanFrequency, setProviderPlanFrequency] =
    useState<CreateIyzicoPricingPlanInput['paymentInterval']>('MONTHLY');
  const [providerPlanIntervalCount, setProviderPlanIntervalCount] = useState(1);

  useEffect(() => {
    if (!editingPlan) return;
    setName(editingPlan.name);
    setDescription(editingPlan.description ?? '');
    setShopifyVariantGid(editingPlan.shopifyVariantGid ?? '');
    setIyzicoProductReferenceCode(editingPlan.iyzicoProductReferenceCode ?? '');
    setIyzicoPricingPlanReferenceCode(editingPlan.iyzicoPricingPlanReferenceCode ?? '');
    setShippingPriceTry(
      editingPlan.shippingPriceMinor === undefined
        ? ''
        : (editingPlan.shippingPriceMinor / 100).toFixed(2),
    );
    setActive(editingPlan.active);
    setFormError(undefined);
  }, [editingPlan]);

  const selectedProduct = catalogQuery.data?.shopifyProducts.find((product) =>
    product.variants.some((variant) => variant.id === shopifyVariantGid),
  );
  const selectedVariant = selectedProduct?.variants.find(
    (variant) => variant.id === shopifyVariantGid,
  );
  const selectedPricingPlan = catalogQuery.data?.iyzicoPricingPlans.find(
    (plan) => plan.referenceCode === iyzicoPricingPlanReferenceCode,
  );
  const visiblePricingPlans =
    catalogQuery.data?.iyzicoPricingPlans.filter(
      (plan) => plan.productReferenceCode === iyzicoProductReferenceCode,
    ) ?? [];
  const shippingPriceMinor = liraToMinor(shippingPriceTry);
  const pricesMatch =
    selectedVariant !== undefined &&
    selectedPricingPlan !== undefined &&
    shippingPriceMinor !== undefined &&
    selectedVariant.amountMinor + shippingPriceMinor === selectedPricingPlan.amountMinor;

  useEffect(() => {
    if (!selectedVariant || !selectedPricingPlan) return;
    const difference = selectedPricingPlan.amountMinor - selectedVariant.amountMinor;
    if (difference >= 0) setShippingPriceTry((difference / 100).toFixed(2));
  }, [selectedPricingPlan, selectedVariant]);

  const upsertMutation = useMutation({
    mutationFn: (input: AdminPlanInput) => api.admin.upsertPlan(input),
    onSuccess: async () => {
      setName('');
      setDescription('');
      setFormError(undefined);
      onCancelEdit();
      await queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
  const createProductMutation = useMutation({
    mutationFn: () =>
      api.admin.createIyzicoProduct({
        name: providerProductName,
        ...(providerProductDescription.trim()
          ? { description: providerProductDescription.trim() }
          : {}),
      }),
    onSuccess: async (product) => {
      setIyzicoProductReferenceCode(product.referenceCode);
      setIyzicoPricingPlanReferenceCode('');
      setProviderProductName('');
      setProviderProductDescription('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'plan-catalog'] });
    },
  });
  const createPricingPlanMutation = useMutation({
    mutationFn: () => {
      const amountMinor = liraToMinor(providerPlanAmountTry);
      if (amountMinor === undefined || amountMinor <= 0) {
        throw new Error('Geçerli bir fiyat girin.');
      }
      return api.admin.createIyzicoPricingPlan({
        productReferenceCode: iyzicoProductReferenceCode,
        name: providerPlanName,
        amountMinor,
        currency: 'TRY',
        paymentInterval: providerPlanFrequency,
        paymentIntervalCount: providerPlanIntervalCount,
      });
    },
    onSuccess: async (plan) => {
      setIyzicoPricingPlanReferenceCode(plan.referenceCode);
      setProviderPlanName('');
      setProviderPlanAmountTry('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'plan-catalog'] });
    },
  });

  const submitPlan = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(undefined);
    if (!selectedProduct || !selectedVariant || !selectedPricingPlan) {
      setFormError('Shopify varyantını ve iyzico fiyat planını seçin.');
      return;
    }
    if (selectedPricingPlan.productReferenceCode !== iyzicoProductReferenceCode) {
      setFormError('Seçilen iyzico ürünü ile fiyat planı eşleşmiyor.');
      return;
    }
    if (!selectedVariant.availableForSale) {
      setFormError('Satışa kapalı Shopify varyantı etkin bir plana bağlanamaz.');
      return;
    }
    if (!pricesMatch || shippingPriceMinor === undefined) {
      setFormError('Shopify ürün fiyatı + kargo payı, iyzico toplam tutarına eşit olmalıdır.');
      return;
    }
    const shopifyProductId = selectedProduct.id.split('/').at(-1);
    const shopifyVariantId = selectedVariant.id.split('/').at(-1);
    if (!shopifyProductId || !shopifyVariantId) {
      setFormError('Shopify kimlikleri geçersiz.');
      return;
    }
    upsertMutation.mutate({
      ...(editingPlan ? { planId: editingPlan.id } : {}),
      name,
      ...(description.trim() ? { description: description.trim() } : {}),
      shopifyProductId,
      shopifyProductGid: selectedProduct.id,
      shopifyVariantId,
      shopifyVariantGid: selectedVariant.id,
      iyzicoProductReferenceCode,
      iyzicoPricingPlanReferenceCode: selectedPricingPlan.referenceCode,
      frequency: selectedPricingPlan.paymentInterval,
      intervalCount: selectedPricingPlan.paymentIntervalCount,
      amountMinor: selectedPricingPlan.amountMinor,
      currency: 'TRY',
      shippingPriceMinor,
      shippingIncluded: true,
      active,
      version: editingPlan?.version ?? 1,
    });
  };

  if (catalogQuery.isPending) return <PageLoader label="Shopify ve iyzico kataloğu alınıyor…" />;
  if (catalogQuery.isError) {
    return (
      <ErrorState
        message={getErrorMessage(catalogQuery.error)}
        onRetry={() => void catalogQuery.refetch()}
        title="Plan kataloğu alınamadı"
      />
    );
  }

  return (
    <Card className="plan-editor">
      <div className="card-title-row">
        <div>
          <p className="eyebrow">
            {editingPlan ? `v${editingPlan.version} düzenleniyor` : 'Yeni eşleme'}
          </p>
          <h2>{editingPlan ? 'Yeni plan versiyonu oluştur' : 'Abonelik planı oluştur'}</h2>
        </div>
        <Plus aria-hidden="true" size={22} />
      </div>
      <form className="plan-editor__form" onSubmit={submitPlan}>
        <div className="plan-editor__fields">
          <FormField htmlFor="plan-name" label="Plan adı" required>
            <input
              id="plan-name"
              maxLength={160}
              onChange={(event) => setName(event.target.value)}
              required
              value={name}
            />
          </FormField>
          <FormField htmlFor="plan-description" label="Açıklama">
            <input
              id="plan-description"
              maxLength={1_000}
              onChange={(event) => setDescription(event.target.value)}
              value={description}
            />
          </FormField>
          <FormField
            hint="Satışa açık varyantlar plan için kullanılabilir."
            htmlFor="plan-shopify-variant"
            label="Shopify ürün / varyant"
            required
          >
            <select
              id="plan-shopify-variant"
              onChange={(event) => setShopifyVariantGid(event.target.value)}
              required
              value={shopifyVariantGid}
            >
              <option value="">Seçin</option>
              {catalogQuery.data.shopifyProducts.map((product) => (
                <optgroup key={product.id} label={product.title}>
                  {product.variants.map((variant) => (
                    <option
                      disabled={!variant.availableForSale}
                      key={variant.id}
                      value={variant.id}
                    >
                      {variant.title} · {formatMoney(variant.amountMinor)}
                      {variant.sku ? ` · ${variant.sku}` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </FormField>
          <FormField htmlFor="plan-provider-product" label="iyzico ürünü" required>
            <select
              id="plan-provider-product"
              onChange={(event) => {
                setIyzicoProductReferenceCode(event.target.value);
                setIyzicoPricingPlanReferenceCode('');
              }}
              required
              value={iyzicoProductReferenceCode}
            >
              <option value="">Seçin</option>
              {catalogQuery.data.iyzicoProducts.map((product) => (
                <option
                  disabled={product.status !== 'ACTIVE'}
                  key={product.referenceCode}
                  value={product.referenceCode}
                >
                  {product.name} · {product.status}
                </option>
              ))}
            </select>
          </FormField>
          <FormField htmlFor="plan-provider-price" label="iyzico fiyat planı" required>
            <select
              disabled={!iyzicoProductReferenceCode}
              id="plan-provider-price"
              onChange={(event) => setIyzicoPricingPlanReferenceCode(event.target.value)}
              required
              value={iyzicoPricingPlanReferenceCode}
            >
              <option value="">Seçin</option>
              {visiblePricingPlans.map((plan) => (
                <option
                  disabled={plan.status !== 'ACTIVE'}
                  key={plan.referenceCode}
                  value={plan.referenceCode}
                >
                  {plan.name} · {formatMoney(plan.amountMinor)} · {plan.paymentIntervalCount}{' '}
                  {plan.paymentInterval}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            hint="Shopify ürün fiyatına eklenen, toplam tahsilat içindeki kargo payı."
            htmlFor="plan-shipping"
            label="Kargo payı (₺)"
            required
          >
            <input
              id="plan-shipping"
              inputMode="decimal"
              min="0"
              onChange={(event) => setShippingPriceTry(event.target.value)}
              required
              step="0.01"
              type="number"
              value={shippingPriceTry}
            />
          </FormField>
        </div>
        <label className="checkbox-row" htmlFor="plan-active">
          <input
            checked={active}
            id="plan-active"
            onChange={(event) => setActive(event.target.checked)}
            type="checkbox"
          />
          Sağlayıcı doğrulamaları geçerse planı yeni aboneliklere aç
        </label>
        {selectedVariant && selectedPricingPlan ? (
          <div
            className={`price-integrity ${pricesMatch ? 'price-integrity--valid' : 'price-integrity--invalid'}`}
          >
            <span>Shopify: {formatMoney(selectedVariant.amountMinor)}</span>
            <span>
              + Kargo: {shippingPriceMinor === undefined ? '—' : formatMoney(shippingPriceMinor)}
            </span>
            <strong>= iyzico: {formatMoney(selectedPricingPlan.amountMinor)}</strong>
            <Badge tone={pricesMatch ? 'positive' : 'danger'}>
              {pricesMatch ? 'Eşleşiyor' : 'Eşleşmiyor'}
            </Badge>
          </div>
        ) : null}
        {formError ? <InlineNotice tone="danger">{formError}</InlineNotice> : null}
        {upsertMutation.isError ? (
          <InlineNotice tone="danger">{getErrorMessage(upsertMutation.error)}</InlineNotice>
        ) : null}
        {upsertMutation.isSuccess ? (
          <InlineNotice tone="positive">
            Plan v{upsertMutation.data.version} kaydedildi.
          </InlineNotice>
        ) : null}
        <div className="table-actions plan-editor__actions">
          {editingPlan ? (
            <Button
              disabled={upsertMutation.isPending}
              onClick={onCancelEdit}
              type="button"
              variant="ghost"
            >
              Düzenlemeyi bırak
            </Button>
          ) : null}
          <Button loading={upsertMutation.isPending} type="submit">
            Sağlayıcıları doğrula ve{' '}
            {editingPlan ? `v${editingPlan.version + 1} oluştur` : 'planı kaydet'}
          </Button>
        </div>
      </form>

      <details className="provider-builder">
        <summary>iyzico ürün veya fiyat planı oluştur</summary>
        <div className="provider-builder__grid">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              createProductMutation.mutate();
            }}
          >
            <h3>Yeni iyzico ürünü</h3>
            <FormField htmlFor="provider-product-name" label="Ürün adı" required>
              <input
                id="provider-product-name"
                maxLength={150}
                onChange={(event) => setProviderProductName(event.target.value)}
                required
                value={providerProductName}
              />
            </FormField>
            <FormField htmlFor="provider-product-description" label="Açıklama">
              <input
                id="provider-product-description"
                maxLength={500}
                onChange={(event) => setProviderProductDescription(event.target.value)}
                value={providerProductDescription}
              />
            </FormField>
            {createProductMutation.isError ? (
              <InlineNotice tone="danger">
                {getErrorMessage(createProductMutation.error)}
              </InlineNotice>
            ) : null}
            <Button
              loading={createProductMutation.isPending}
              size="small"
              type="submit"
              variant="secondary"
            >
              iyzico ürününü oluştur
            </Button>
          </form>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              createPricingPlanMutation.mutate();
            }}
          >
            <h3>Yeni iyzico fiyat planı</h3>
            <FormField htmlFor="provider-plan-name" label="Plan adı" required>
              <input
                id="provider-plan-name"
                maxLength={150}
                onChange={(event) => setProviderPlanName(event.target.value)}
                required
                value={providerPlanName}
              />
            </FormField>
            <FormField htmlFor="provider-plan-amount" label="Toplam tahsilat (₺)" required>
              <input
                id="provider-plan-amount"
                inputMode="decimal"
                min="0.01"
                onChange={(event) => setProviderPlanAmountTry(event.target.value)}
                required
                step="0.01"
                type="number"
                value={providerPlanAmountTry}
              />
            </FormField>
            <FormField htmlFor="provider-plan-frequency" label="Periyot" required>
              <select
                id="provider-plan-frequency"
                onChange={(event) =>
                  setProviderPlanFrequency(
                    event.target.value as CreateIyzicoPricingPlanInput['paymentInterval'],
                  )
                }
                value={providerPlanFrequency}
              >
                <option value="WEEKLY">Haftalık</option>
                <option value="MONTHLY">Aylık</option>
                <option value="YEARLY">Yıllık</option>
              </select>
            </FormField>
            <FormField htmlFor="provider-plan-count" label="Periyot adedi" required>
              <input
                id="provider-plan-count"
                max={100}
                min={1}
                onChange={(event) => setProviderPlanIntervalCount(event.target.valueAsNumber)}
                required
                type="number"
                value={providerPlanIntervalCount}
              />
            </FormField>
            {createPricingPlanMutation.isError ? (
              <InlineNotice tone="danger">
                {getErrorMessage(createPricingPlanMutation.error)}
              </InlineNotice>
            ) : null}
            <Button
              disabled={!iyzicoProductReferenceCode}
              loading={createPricingPlanMutation.isPending}
              size="small"
              type="submit"
              variant="secondary"
            >
              Fiyat planını oluştur
            </Button>
          </form>
        </div>
      </details>
    </Card>
  );
}

export function AdminPlansPage() {
  const queryClient = useQueryClient();
  const [editingPlan, setEditingPlan] = useState<AdminPlan>();
  const query = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: ({ signal }) => api.admin.listPlans(signal),
  });
  const syncMutation = useMutation({
    mutationFn: () => api.admin.syncPlans(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] }),
  });
  const toggleMutation = useMutation({
    mutationFn: ({ planId, active }: { planId: string; active: boolean }) =>
      api.admin.setPlanActive(planId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] }),
  });

  if (query.isPending) return <PageLoader label="Plan eşlemeleri alınıyor…" />;
  if (query.isError)
    return (
      <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    );

  return (
    <div className="admin-page page-stack">
      <PageHeading
        actions={
          <Button
            loading={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
            variant="secondary"
          >
            <RefreshCw aria-hidden="true" size={17} /> Shopify ürünlerini senkronize et
          </Button>
        }
        description="Shopify varyantı, abonelik periyodu ve tahsilat tutarı arasındaki eşlemeleri yönetin."
        eyebrow="Plan yönetimi"
        title="Kutu planları"
      />
      <InlineNotice title="Fiyat bütünlüğü" tone="info">
        Plan kaydedilmeden önce Shopify varyant fiyatı, ödeme kuruluşu plan fiyatı ve Firestore
        tutarı backend tarafından karşılaştırılmalıdır. Fark sessizce kabul edilmez.
      </InlineNotice>
      {syncMutation.isSuccess ? (
        <InlineNotice tone="positive">{syncMutation.data.message}</InlineNotice>
      ) : null}
      {syncMutation.isError ? (
        <InlineNotice tone="danger">{getErrorMessage(syncMutation.error)}</InlineNotice>
      ) : null}
      <AdminPlanEditor editingPlan={editingPlan} onCancelEdit={() => setEditingPlan(undefined)} />
      <Card className="table-card">
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>Varyant / SKU</th>
                <th>Periyot</th>
                <th>Tutar</th>
                <th>Versiyon</th>
                <th>Durum</th>
                <th>
                  <span className="sr-only">İşlem</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((plan) => (
                <tr key={plan.id}>
                  <td data-label="Plan">
                    <strong>{plan.name}</strong>
                    <small>{plan.shippingIncluded ? 'Kargo dahil' : 'Kargo hariç'}</small>
                  </td>
                  <td data-label="Varyant / SKU">
                    {plan.variantLabel}
                    <small>{plan.sku ?? 'SKU yok'}</small>
                  </td>
                  <td data-label="Periyot">{frequencyLabel(plan)}</td>
                  <td data-label="Tutar">{formatMoney(plan.amountMinor)}</td>
                  <td data-label="Versiyon">v{plan.version}</td>
                  <td data-label="Durum">
                    <Badge tone={plan.active ? 'positive' : 'neutral'}>
                      {plan.active ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </td>
                  <td data-label="İşlem">
                    <div className="table-actions">
                      <Button
                        disabled={
                          !plan.shopifyProductGid ||
                          !plan.shopifyVariantGid ||
                          !plan.iyzicoProductReferenceCode ||
                          !plan.iyzicoPricingPlanReferenceCode ||
                          plan.shippingPriceMinor === undefined
                        }
                        onClick={() => {
                          setEditingPlan(plan);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                        variant="ghost"
                      >
                        Yeni versiyon
                      </Button>
                      <Button
                        disabled={toggleMutation.isPending}
                        onClick={() =>
                          toggleMutation.mutate({ planId: plan.id, active: !plan.active })
                        }
                        size="small"
                        variant="ghost"
                      >
                        {plan.active ? 'Pasife al' : 'Aktifleştir'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function AdminSubscriptionsPage() {
  const [status, setStatus] = useState('');
  const [planId, setPlanId] = useState('');
  const [customer, setCustomer] = useState('');
  const [iyzicoReference, setIyzicoReference] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AdminSubscriptionFilters>({});
  const filters = useMemo(() => appliedFilters, [appliedFilters]);
  const plansQuery = useQuery({
    queryKey: ['admin', 'plans', 'filter-options'],
    queryFn: ({ signal }) => api.admin.listPlans(signal),
  });
  const query = useQuery({
    queryKey: ['admin', 'subscriptions', filters],
    queryFn: ({ signal }) => api.admin.listSubscriptions(filters, signal),
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    const next: AdminSubscriptionFilters = {};
    if (status) next.status = status;
    if (planId) next.planId = planId;
    if (customer.trim()) next.customer = customer.trim();
    if (iyzicoReference.trim()) next.iyzicoReference = iyzicoReference.trim();
    if (from) next.from = istanbulDayBoundary(from);
    if (to) next.to = istanbulDayBoundary(to, true);
    setAppliedFilters(next);
  };

  return (
    <div className="admin-page page-stack">
      <PageHeading
        description="Durum, plan veya müşteri bilgisiyle güvenli abonelik kayıtlarını bulun."
        eyebrow="Müşteri operasyonu"
        title="Abonelikler"
      />
      <Card className="filter-card">
        <form className="filter-form filter-form--subscriptions" onSubmit={applyFilters}>
          <FormField htmlFor="admin-sub-status" label="Durum">
            <select
              id="admin-sub-status"
              onChange={(event) => setStatus(event.target.value)}
              value={status}
            >
              <option value="">Tümü</option>
              <option value="ACTIVE">Aktif</option>
              <option value="PAST_DUE">Ödeme gerekli</option>
              <option value="CANCELED">İptal edilmiş</option>
              <option value="SYNC_ERROR">Senkronizasyon hatası</option>
            </select>
          </FormField>
          <FormField htmlFor="admin-sub-plan" label="Plan">
            <select
              id="admin-sub-plan"
              onChange={(event) => setPlanId(event.target.value)}
              value={planId}
            >
              <option value="">Tümü</option>
              {plansQuery.data?.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} · v{plan.version}
                </option>
              ))}
            </select>
          </FormField>
          <FormField htmlFor="admin-sub-customer" label="Müşteri / Shopify ID">
            <input
              id="admin-sub-customer"
              onChange={(event) => setCustomer(event.target.value)}
              placeholder="Ad, e-posta veya ID"
              value={customer}
            />
          </FormField>
          <FormField htmlFor="admin-sub-iyzico" label="iyzico abonelik referansı">
            <input
              id="admin-sub-iyzico"
              onChange={(event) => setIyzicoReference(event.target.value)}
              placeholder="subscriptionReferenceCode"
              value={iyzicoReference}
            />
          </FormField>
          <FormField htmlFor="admin-sub-from" label="Başlangıç tarihi">
            <input
              id="admin-sub-from"
              onChange={(event) => setFrom(event.target.value)}
              type="date"
              value={from}
            />
          </FormField>
          <FormField htmlFor="admin-sub-to" label="Bitiş tarihi">
            <input
              id="admin-sub-to"
              min={from || undefined}
              onChange={(event) => setTo(event.target.value)}
              type="date"
              value={to}
            />
          </FormField>
          <Button type="submit">
            <Search aria-hidden="true" size={17} /> Filtrele
          </Button>
        </form>
      </Card>
      {query.isPending ? <PageLoader label="Abonelik kayıtları taranıyor…" /> : null}
      {query.isError ? (
        <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : null}
      {query.data && query.data.length === 0 ? (
        <EmptyState
          message="Seçtiğiniz filtrelerle eşleşen abonelik bulunamadı."
          title="Sonuç yok"
        />
      ) : null}
      {query.data && query.data.length > 0 ? (
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Müşteri</th>
                  <th>Plan</th>
                  <th>Durum</th>
                  <th>Sonraki tahsilat</th>
                  <th>Tutar</th>
                  <th>
                    <span className="sr-only">Detay</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((subscription) => (
                  <tr key={subscription.id}>
                    <td data-label="Müşteri">
                      <strong>{subscription.customer.name}</strong>
                      <small>
                        {subscription.customer.email} · {subscription.customer.shopifyCustomerId}
                      </small>
                    </td>
                    <td data-label="Plan">
                      {subscription.plan.name}
                      <small>{subscription.plan.variantLabel}</small>
                    </td>
                    <td data-label="Durum">
                      <Badge tone={subscriptionStatusTone(subscription.status)}>
                        {subscriptionStatusLabels[subscription.status]}
                      </Badge>
                    </td>
                    <td data-label="Sonraki tahsilat">{formatDate(subscription.nextPaymentAt)}</td>
                    <td data-label="Tutar">{formatMoney(subscription.amountMinor)}</td>
                    <td data-label="Detay">
                      <ButtonLink
                        size="small"
                        to={`/admin/subscriptions/${subscription.id}`}
                        variant="ghost"
                      >
                        İncele <ArrowRight aria-hidden="true" size={15} />
                      </ButtonLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

export function AdminSubscriptionDetailPage() {
  const { id } = useParams();
  const query = useQuery({
    queryKey: ['admin', 'subscription', id],
    queryFn: ({ signal }) => {
      if (!id) throw new Error('Abonelik kimliği eksik.');
      return api.admin.getSubscription(id, signal);
    },
    enabled: Boolean(id),
  });

  if (query.isPending) return <PageLoader label="Abonelik operasyon kaydı açılıyor…" />;
  if (query.isError)
    return (
      <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    );
  const subscription = query.data;

  return (
    <div className="admin-page page-stack">
      <Link className="back-link" to="/admin/subscriptions">
        <ArrowLeft aria-hidden="true" size={17} /> Abonelikler
      </Link>
      <PageHeading
        actions={
          <Badge tone={subscriptionStatusTone(subscription.status)}>
            {subscriptionStatusLabels[subscription.status]}
          </Badge>
        }
        description={`${subscription.customer.name} · ${subscription.customer.email}`}
        eyebrow={subscription.reference}
        title={subscription.plan.name}
      />
      <div className="detail-grid detail-grid--admin">
        <Card className="detail-card">
          <h2>Müşteri ve plan</h2>
          <dl className="definition-list">
            <DefinitionItem
              label="Shopify customer ID"
              value={<code>{subscription.customer.shopifyCustomerId}</code>}
            />
            <DefinitionItem
              label="Plan"
              value={`${subscription.plan.name} · ${subscription.plan.variantLabel}`}
            />
            <DefinitionItem label="Tutar" value={formatMoney(subscription.amountMinor)} />
            <DefinitionItem label="Periyot" value={frequencyLabel(subscription.plan)} />
          </dl>
        </Card>
        <Card className="detail-card">
          <h2>Durum karşılaştırması</h2>
          <dl className="definition-list">
            <DefinitionItem label="Firestore" value={<code>{subscription.status}</code>} />
            <DefinitionItem
              label="Ödeme sağlayıcısı"
              value={<code>{subscription.providerStatus}</code>}
            />
            <DefinitionItem label="Başlangıç" value={formatDateTime(subscription.startedAt)} />
            <DefinitionItem
              label="Sonraki tahsilat"
              value={formatDateTime(subscription.nextPaymentAt)}
            />
          </dl>
        </Card>
        <Card className="detail-card">
          <h2>Teslimat snapshot</h2>
          <p>
            <strong>
              {subscription.shippingAddress.firstName} {subscription.shippingAddress.lastName}
            </strong>
          </p>
          <p>{formatAddress(subscription.shippingAddress)}</p>
          <small>Telefon ve tam adres loglara yazılmamalıdır.</small>
        </Card>
      </div>
      <section>
        <div className="section-heading">
          <div>
            <p className="eyebrow">Döngüler</p>
            <h2>Tahsilat ve Shopify siparişleri</h2>
          </div>
        </div>
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Ödeme</th>
                  <th>Shopify sync</th>
                  <th>Deneme</th>
                  <th>Sipariş</th>
                </tr>
              </thead>
              <tbody>
                {subscription.cycles.map((cycle) => (
                  <tr key={cycle.id}>
                    <td data-label="Cycle">
                      <code>{cycle.id}</code>
                      <small>#{cycle.cycleNumber}</small>
                    </td>
                    <td data-label="Ödeme">
                      <code>{cycle.paymentStatus}</code>
                      <small>{formatMoney(cycle.amountMinor)}</small>
                    </td>
                    <td data-label="Shopify sync">
                      <code>{cycle.shopifySyncStatus}</code>
                    </td>
                    <td data-label="Deneme">{cycle.attemptCount}</td>
                    <td data-label="Sipariş">
                      {cycle.order?.adminUrl ? (
                        <a
                          className="table-link"
                          href={cycle.order.adminUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {cycle.order.name}
                          <ExternalLink aria-hidden="true" size={14} />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
      <section>
        <div className="section-heading">
          <div>
            <p className="eyebrow">Provider olayları</p>
            <h2>Webhook geçmişi</h2>
          </div>
        </div>
        <Card>
          <ul className="event-list">
            {subscription.webhookEvents.map((event) => (
              <li key={event.id}>
                <div>
                  <strong>{event.eventType}</strong>
                  <code>{event.id}</code>
                </div>
                <Badge
                  tone={
                    event.status === 'PROCESSED'
                      ? 'positive'
                      : event.status === 'FAILED'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  {event.status}
                </Badge>
                <small>{formatDateTime(event.receivedAt)}</small>
              </li>
            ))}
          </ul>
        </Card>
      </section>
      <section>
        <div className="section-heading">
          <div>
            <p className="eyebrow">İzlenebilirlik</p>
            <h2>Audit geçmişi</h2>
          </div>
        </div>
        <Card>
          {(subscription.auditEntries ?? []).length === 0 ? (
            <div className="compact-empty">Bu abonelik için audit kaydı bulunmuyor.</div>
          ) : (
            <ul className="event-list">
              {(subscription.auditEntries ?? []).map((entry) => (
                <li key={entry.id}>
                  <div>
                    <strong>{entry.action}</strong>
                    <span>
                      {entry.actorLabel} · {entry.resourceType}
                    </span>
                  </div>
                  <code>{entry.correlationId}</code>
                  <small>{formatDateTime(entry.createdAt)}</small>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}

export function AdminPaymentsPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<{
    issue: PaymentIssue;
    action: 'retry' | 'resolve';
  } | null>(null);
  const query = useQuery({
    queryKey: ['admin', 'payments'],
    queryFn: ({ signal }) => api.admin.listPaymentIssues(signal),
  });
  const mutation = useMutation({
    mutationFn: ({ issue, action }: { issue: PaymentIssue; action: 'retry' | 'resolve' }) =>
      action === 'retry'
        ? api.admin.retryPayment(issue.cycleId)
        : api.admin.resolveAmbiguousPaymentRetry(issue.cycleId),
    onSuccess: async () => {
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });

  return (
    <AdminIssuePageShell
      description="Başarısız tahsilatları inceleyin. Yeniden deneme aynı ödeme referansı için eşzamanlı olarak yalnızca bir kez başlatılmalıdır."
      eyebrow="Tahsilat operasyonu"
      title="Ödeme sorunları"
    >
      {query.isPending ? <PageLoader /> : null}
      {query.isError ? (
        <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : null}
      {query.data?.length === 0 ? (
        <EmptyState message="Takip bekleyen başarısız tahsilat yok." title="Ödeme kuyruğu temiz" />
      ) : null}
      {query.data && query.data.length > 0 ? (
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Müşteri / plan</th>
                  <th>Hata</th>
                  <th>Son deneme</th>
                  <th>Gereksinim</th>
                  <th>
                    <span className="sr-only">İşlem</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((issue) => (
                  <tr key={issue.cycleId}>
                    <td data-label="Müşteri / plan">
                      <strong>{issue.customerName}</strong>
                      <small>
                        {issue.planName}
                        <br />
                        {issue.subscriptionId}
                      </small>
                    </td>
                    <td data-label="Hata">
                      <code>{issue.errorCode}</code>
                      <small>{issue.errorMessage}</small>
                    </td>
                    <td data-label="Son deneme">{formatDateTime(issue.lastAttemptAt)}</td>
                    <td data-label="Gereksinim">
                      <Badge tone={issue.reviewRequired ? 'danger' : 'warning'}>
                        {issue.reviewRequired ? 'Sağlayıcı kontrolü' : 'Kart güncelleme'}
                      </Badge>
                    </td>
                    <td data-label="İşlem">
                      <div className="table-actions">
                        <Button
                          disabled={!issue.retryAllowed}
                          onClick={() => setSelected({ issue, action: 'retry' })}
                          size="small"
                          variant="secondary"
                        >
                          Yeniden dene
                        </Button>
                        {issue.reviewRequired ? (
                          <Button
                            onClick={() => setSelected({ issue, action: 'resolve' })}
                            size="small"
                            variant="ghost"
                          >
                            Kontrolü kaydet
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
      {mutation.isError ? (
        <InlineNotice tone="danger">{getErrorMessage(mutation.error)}</InlineNotice>
      ) : null}
      <ConfirmDialog
        busy={mutation.isPending}
        confirmLabel={
          selected?.action === 'resolve' ? 'Tahsilat olmadığını kaydet' : 'Tahsilatı yeniden dene'
        }
        description={
          selected?.action === 'resolve'
            ? 'Yalnız iyzico paneli ve server-side detail kayıtlarında bu denemeye ait yeni bir tahsilat bulunmadığını ikinci kişiyle doğruladıysanız devam edin. Bu işlem para iadesi veya tahsilat yapmaz; kaydı kontrollü retry için yeniden açar.'
            : 'Bu işlem yeni bir ödeme referansı üretmemeli; mevcut başarısız dönem için sağlayıcının resmi retry akışını başlatmalı ve sonucu webhook/detail sorgusuyla doğrulamalıdır.'
        }
        onClose={() => setSelected(null)}
        onConfirm={() => selected && mutation.mutate(selected)}
        open={Boolean(selected)}
        title={
          selected?.action === 'resolve'
            ? 'Sağlayıcı kontrolünü kaydet'
            : 'Yeniden denemeyi onaylıyor musunuz?'
        }
      />
    </AdminIssuePageShell>
  );
}

export function AdminOrderSyncPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<{
    issue: OrderSyncIssue;
    action: 'retry' | 'permanent';
  } | null>(null);
  const query = useQuery({
    queryKey: ['admin', 'order-sync'],
    queryFn: ({ signal }) => api.admin.listOrderSyncIssues(signal),
  });
  const mutation = useMutation({
    mutationFn: ({ issue, action }: { issue: OrderSyncIssue; action: 'retry' | 'permanent' }) =>
      action === 'retry'
        ? api.admin.retryOrderSync(issue.cycleId)
        : api.admin.markOrderSyncPermanent(issue.cycleId),
    onSuccess: async () => {
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'order-sync'] });
    },
  });

  return (
    <AdminIssuePageShell
      description="Başarılı tahsilata ait Shopify sipariş aktarımını izleyin. Manuel deneme yeni ödeme almaz ve duplicate sipariş kontrolü yapmalıdır."
      eyebrow="Shopify operasyonu"
      title="Sipariş aktarım sorunları"
    >
      <InlineNotice title="İkinci katman idempotency" tone="info">
        Retry öncesinde Shopify'da aynı iyzico order reference ile oluşturulmuş sipariş aranmalıdır.
      </InlineNotice>
      {query.isPending ? <PageLoader /> : null}
      {query.isError ? (
        <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : null}
      {query.data?.length === 0 ? (
        <EmptyState message="Takip bekleyen sipariş aktarımı yok." title="Aktarım kuyruğu temiz" />
      ) : null}
      {query.data && query.data.length > 0 ? (
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Cycle / abonelik</th>
                  <th>Dış referans</th>
                  <th>Hata</th>
                  <th>Deneme</th>
                  <th>Son deneme</th>
                  <th>
                    <span className="sr-only">İşlemler</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((issue) => (
                  <tr key={issue.cycleId}>
                    <td data-label="Cycle / abonelik">
                      <code>{issue.cycleId}</code>
                      <small>{issue.subscriptionId}</small>
                    </td>
                    <td data-label="Dış referans">
                      <code>{issue.orderReference}</code>
                    </td>
                    <td data-label="Hata">{issue.errorMessage}</td>
                    <td data-label="Deneme">{issue.attemptCount}</td>
                    <td data-label="Son deneme">{formatDateTime(issue.lastAttemptAt)}</td>
                    <td data-label="İşlemler">
                      <div className="table-actions">
                        <Button
                          disabled={!issue.retryAllowed}
                          onClick={() => setSelected({ issue, action: 'retry' })}
                          size="small"
                          variant="secondary"
                        >
                          Tekrar aktar
                        </Button>
                        <Button
                          disabled={!issue.canMarkPermanent}
                          onClick={() => setSelected({ issue, action: 'permanent' })}
                          size="small"
                          variant="ghost"
                        >
                          Kalıcı hata
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
      {mutation.isError ? (
        <InlineNotice tone="danger">{getErrorMessage(mutation.error)}</InlineNotice>
      ) : null}
      <ConfirmDialog
        busy={mutation.isPending}
        confirmLabel={
          selected?.action === 'permanent' ? 'Operasyon incelemesine al' : 'Sipariş aktarımını dene'
        }
        dangerous={selected?.action === 'permanent'}
        description={
          selected?.action === 'permanent'
            ? 'Bu cycle otomatik retry kuyruğundan çıkarılacak ve manuel operasyon incelemesi gerektirecek.'
            : 'Yalnızca mevcut başarılı ödeme için Shopify sipariş aktarımı yeniden denenecek; yeni tahsilat oluşturulmayacak.'
        }
        onClose={() => setSelected(null)}
        onConfirm={() => selected && mutation.mutate(selected)}
        open={Boolean(selected)}
        title={
          selected?.action === 'permanent'
            ? 'Kalıcı hata olarak işaretlensin mi?'
            : 'Aktarım yeniden denensin mi?'
        }
      />
    </AdminIssuePageShell>
  );
}

function AdminIssuePageShell({
  title,
  eyebrow,
  description,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="admin-page page-stack">
      <PageHeading description={description} eyebrow={eyebrow} title={title} />
      {children}
    </div>
  );
}

export function AdminCustomersPage() {
  const query = useQuery({
    queryKey: ['admin', 'customers'],
    queryFn: ({ signal }) => api.admin.listCustomers(signal),
  });
  if (query.isPending) return <PageLoader label="Müşteri kayıtları alınıyor…" />;
  if (query.isError)
    return (
      <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    );
  return (
    <div className="admin-page page-stack">
      <PageHeading
        description="Shopify kimliğiyle eşleşmiş portal müşterilerini ve aktif abonelik sayılarını görüntüleyin."
        eyebrow="Müşteri dizini"
        title="Müşteriler"
      />
      {query.data.length === 0 ? (
        <EmptyState message="Henüz portal müşterisi bulunmuyor." title="Müşteri yok" />
      ) : (
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Müşteri</th>
                  <th>Firebase UID</th>
                  <th>Shopify ID</th>
                  <th>Aktif abonelik</th>
                  <th>Son giriş</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((customer) => (
                  <tr key={customer.uid}>
                    <td data-label="Müşteri">
                      <strong>{customer.name}</strong>
                      <small>{customer.email}</small>
                    </td>
                    <td data-label="Firebase UID">
                      <code>{customer.uid}</code>
                    </td>
                    <td data-label="Shopify ID">
                      <code>{customer.shopifyCustomerId}</code>
                    </td>
                    <td data-label="Aktif abonelik">{customer.activeSubscriptionCount}</td>
                    <td data-label="Son giriş">{formatDateTime(customer.lastLoginAt)}</td>
                    <td data-label="Durum">
                      <Badge tone={customer.status === 'ACTIVE' ? 'positive' : 'neutral'}>
                        {customer.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export function AdminAuditPage() {
  const query = useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: ({ signal }) => api.admin.listAudit(signal),
  });
  if (query.isPending) return <PageLoader label="Denetim kayıtları alınıyor…" />;
  if (query.isError)
    return (
      <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    );
  return (
    <div className="admin-page page-stack">
      <PageHeading
        description="Kritik müşteri, yönetici ve sistem işlemlerini correlation ID ile izleyin."
        eyebrow="Güvenlik ve izlenebilirlik"
        title="Denetim kayıtları"
      />
      <InlineNotice tone="info">
        Kart bilgisi, token, secret, tam adres ve telefon denetim kayıtlarında bulunmamalıdır.
      </InlineNotice>
      {query.data.length === 0 ? (
        <EmptyState message="Gösterilecek denetim kaydı yok." title="Kayıt yok" />
      ) : (
        <Card className="table-card">
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Zaman</th>
                  <th>Aktör</th>
                  <th>İşlem</th>
                  <th>Kaynak</th>
                  <th>Correlation ID</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((entry) => (
                  <tr key={entry.id}>
                    <td data-label="Zaman">{formatDateTime(entry.createdAt)}</td>
                    <td data-label="Aktör">
                      <Badge tone={entry.actorType === 'SYSTEM' ? 'info' : 'neutral'}>
                        {entry.actorType}
                      </Badge>
                      <small>{entry.actorLabel}</small>
                    </td>
                    <td data-label="İşlem">
                      <code>{entry.action}</code>
                    </td>
                    <td data-label="Kaynak">
                      {entry.resourceType}
                      <small>{entry.resourceId}</small>
                    </td>
                    <td data-label="Correlation ID">
                      <code>{entry.correlationId}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: ({ signal }) => api.admin.getSettings(signal),
  });
  const form = useForm<AdminSettingsInput>({
    resolver: zodResolver(adminSettingsInputSchema),
    defaultValues: { sendShopifyReceipt: false, supportEmail: '' },
  });
  useEffect(() => {
    if (query.data)
      form.reset({
        sendShopifyReceipt: query.data.sendShopifyReceipt,
        supportEmail: query.data.supportEmail,
      });
  }, [form, query.data]);
  const mutation = useMutation({
    mutationFn: (input: AdminSettingsInput) => api.admin.updateSettings(input),
    onSuccess: async (data) => {
      form.reset({ sendShopifyReceipt: data.sendShopifyReceipt, supportEmail: data.supportEmail });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
  if (query.isPending) return <PageLoader label="Ortam ayarları alınıyor…" />;
  if (query.isError)
    return (
      <ErrorState message={getErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    );
  return (
    <div className="admin-page narrow-admin page-stack">
      <PageHeading
        description="Gizli anahtarlar bu ekranda gösterilmez veya tarayıcıya gönderilmez."
        eyebrow="Portal yapılandırması"
        title="Ayarlar"
      />
      <Card className="environment-card">
        <div>
          <span>Ortam</span>
          <strong>{query.data.environment}</strong>
        </div>
        <div>
          <span>Para birimi</span>
          <strong>{query.data.defaultCurrency}</strong>
        </div>
        <div>
          <span>Saat dilimi</span>
          <strong>{query.data.timezone}</strong>
        </div>
        <div>
          <span>App Check</span>
          <Badge tone={query.data.appCheckEnforced ? 'positive' : 'danger'}>
            {query.data.appCheckEnforced ? 'Zorunlu' : 'Kapalı'}
          </Badge>
        </div>
      </Card>
      {!query.data.appCheckEnforced ? (
        <InlineNotice title="Güvenlik uyarısı" tone="danger">
          Production portal API çağrılarında Firebase App Check zorunlu olmalıdır.
        </InlineNotice>
      ) : null}
      <form noValidate onSubmit={form.handleSubmit((input) => mutation.mutate(input))}>
        <Card className="form-section">
          <h2>Müşteri iletişimi</h2>
          <FormField
            error={form.formState.errors.supportEmail?.message}
            htmlFor="support-email"
            label="Destek e-postası"
            required
          >
            <input
              autoComplete="email"
              id="support-email"
              type="email"
              {...form.register('supportEmail')}
            />
          </FormField>
          <label className="checkbox-card">
            <input type="checkbox" {...form.register('sendShopifyReceipt')} />
            <span>
              <strong>Shopify sipariş onay e-postasını gönder</strong>
              <small>Staging için varsayılan kapalı, production için açık olmalıdır.</small>
            </span>
          </label>
          {mutation.isSuccess ? (
            <InlineNotice tone="positive">Ayarlar güvenle kaydedildi.</InlineNotice>
          ) : null}
          {mutation.isError ? (
            <InlineNotice tone="danger">{getErrorMessage(mutation.error)}</InlineNotice>
          ) : null}
          <div className="form-actions">
            <Button loading={mutation.isPending} type="submit">
              <ShieldCheck aria-hidden="true" size={17} /> Ayarları kaydet
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}

export function AdminRouteFallback() {
  return (
    <div className="admin-page">
      <Card className="state state--error">
        <AlertTriangle aria-hidden="true" size={30} />
        <h1>Yönetim sayfası bulunamadı</h1>
        <ButtonLink to="/admin">Genel görünüme dön</ButtonLink>
      </Card>
    </div>
  );
}
